import React, { useEffect, useRef } from "react";
import { AlertTriangle } from "lucide-react";

// Bug 4 audit: el banner de error vivía al pie del modal y si el form estaba
// scrolleado arriba, el usuario clickeaba "Crear Viaje" y no veía nada — parecía
// que el botón no respondía. Ahora hacemos scrollIntoView al banner cada vez
// que `error` cambia. Además renderizamos errores multi-línea como lista para
// que un submit con varios problemas muestre todos juntos (bug 3).
function ErrorBanner({ error }) {
  const ref = useRef(null);
  useEffect(() => {
    if (error && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);
  const lines = String(error).split("\n").filter(Boolean);
  return (
    <div
      ref={ref}
      className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm"
    >
      {lines.length > 1 ? (
        <ul className="list-disc pl-5 space-y-1">
          {lines.map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </ul>
      ) : (
        error
      )}
    </div>
  );
}

// Pinned-above-buttons error/warning area: international passport block,
// owner-only soft warning, generic error, AOG conflict, and schedule
// conflict modal with "force anyway" action.
function ConflictBanners({
  hasIntlLeg,
  ownerIntlMissing,
  userRole,
  internationalPassportConflicts,
  error,
  aogConflict,
  scheduleConflict,
  forceSchedule,
  setForceSchedule,
  setScheduleConflict,
  submitting,
  onForceSubmit,
  t,
}) {
  return (
    <div className="px-6 flex-shrink-0 space-y-2">
      {internationalPassportConflicts.length > 0 && userRole !== "owner" && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl space-y-1.5">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-red-400 text-sm font-semibold">
              {t("newTrip.intlPassportBlock")}
            </span>
          </div>
          {internationalPassportConflicts.map((msg, i) => (
            <p key={i} className="text-xs text-slate-300 pl-6">
              {msg}
            </p>
          ))}
          <p className="text-xs text-slate-400 pl-6">{t("newTrip.intlPassportBlockHint")}</p>
        </div>
      )}
      {userRole === "owner" &&
        hasIntlLeg &&
        (() => {
          const items = [...ownerIntlMissing, ...internationalPassportConflicts];
          if (items.length === 0) return null;
          return (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl space-y-1.5">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <span className="text-amber-400 text-sm font-semibold">
                  {t("newTrip.intlPassportOwnerWarning")}
                </span>
              </div>
              {items.map((msg, i) => (
                <p key={i} className="text-xs text-slate-300 pl-6">
                  {msg}
                </p>
              ))}
              <p className="text-xs text-slate-400 pl-6">
                {t("newTrip.intlPassportOwnerWarningHint")}
              </p>
            </div>
          );
        })()}
      {error && <ErrorBanner error={error} />}
      {aogConflict && aogConflict.length > 0 && (
        <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-xl space-y-1">
          <span className="text-red-400 text-sm font-semibold">{t("newTrip.aogActiveEvents")}</span>
          {aogConflict.map((c, i) => (
            <p key={i} className="text-xs text-slate-300">
              {c.title} ({c.start_date} {"→"} {c.end_date})
            </p>
          ))}
          <p className="text-xs text-yellow-400">{t("newTrip.forceCreateHint")}</p>
        </div>
      )}
      {scheduleConflict && scheduleConflict.length > 0 && !forceSchedule && (
        <div className="p-4 bg-yellow-500/5 border border-yellow-500/30 rounded-xl space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-400 text-sm font-semibold">
                {t("newTrip.scheduleConflictTitle")}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {t("newTrip.scheduleConflictMessage", { count: scheduleConflict.length })}
              </p>
            </div>
          </div>
          <div className="space-y-2 pl-6">
            {scheduleConflict.map((c, i) => (
              <div key={i} className="bg-slate-800/60 rounded-lg px-3 py-2 text-xs">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-mono text-yellow-400">#{c.trip_number}</span>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${c.status === "confirmed" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}
                  >
                    {t(`status.${c.status}`, { defaultValue: c.status })}
                  </span>
                </div>
                <p className="text-white font-medium">{c.route}</p>
                <p className="text-slate-400 mt-0.5">
                  {t("newTrip.completionMessage")}{" "}
                  <span className="text-white font-medium">{c.trip_end}</span>
                  {c.last_arrival && (
                    <span>
                      {" "}
                      — {t("newTrip.lastArrivalLabel")}:{" "}
                      {new Date(c.last_arrival).toLocaleString("es-MX", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </span>
                  )}
                </p>
              </div>
            ))}
          </div>
          <div className="pl-6 flex gap-2">
            <button
              type="button"
              onClick={() => setScheduleConflict(null)}
              className="px-3 py-1.5 text-xs bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => {
                setForceSchedule(true);
                setScheduleConflict(null);
                onForceSubmit();
              }}
              className="px-3 py-1.5 text-xs bg-yellow-500/20 border border-yellow-500/50 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors font-semibold disabled:opacity-50"
            >
              {submitting ? t("newTrip.creating") : t("newTrip.forceCreateAnyway")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(ConflictBanners);
